import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const saveTokens = (token, refreshToken) => {
    // Still save to localStorage as fallback (will be removed in future cleanup).
    // The httpOnly cookie is now the primary auth mechanism — set by the server.
    localStorage.setItem("token", token);
    if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
  };

  const clearTokens = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("auth_provider");
    localStorage.removeItem("cognito_refresh_token");
    // Also clear httpOnly cookies via server endpoint
    api.post("/auth/logout").catch(() => {});
  };

  const refreshAccessToken = useCallback(async () => {
    try {
      // Cookie-based: the httpOnly refresh cookie is sent automatically.
      // Also send header as fallback for legacy sessions.
      const headers = {};
      const authProvider = localStorage.getItem("auth_provider");
      const refreshToken = authProvider === "cognito"
        ? localStorage.getItem("cognito_refresh_token") || localStorage.getItem("refresh_token")
        : localStorage.getItem("refresh_token");
      if (refreshToken) {
        headers.Authorization = `Bearer ${refreshToken}`;
      }
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {}, {
        headers,
        withCredentials: true,  // Send httpOnly cookie
      });
      // Handle Cognito response (has cognito_token) vs legacy
      const primaryToken = res.data.cognito_token || res.data.token;
      const newRefresh = res.data.cognito_refresh_token || res.data.refresh_token;
      saveTokens(primaryToken, newRefresh);
      if (res.data.cognito_token) {
        localStorage.setItem("auth_provider", "cognito");
      }
      setUser(res.data.user);
      return true;
    } catch (err) {
      if (err.response?.status === 401) {
        try { navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout-diagnostic`, JSON.stringify({ reason: "refresh_401_in_context", path: window.location.pathname })); } catch {}
        clearTokens();
        setUser(null);
      }
      return false;
    }
  }, []);

  useEffect(() => {
    // Try /auth/me — httpOnly cookie (if set) will be sent automatically.
    // Also works with legacy localStorage token via the request interceptor.
    const token = localStorage.getItem("token");
    // Attempt auth/me if we have a cookie OR a localStorage token
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(async (err) => {
        if (err.response?.status === 401) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            if (token) {
              try { navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout-diagnostic`, JSON.stringify({ reason: "me_401_refresh_failed", path: window.location.pathname })); } catch {}
            }
            clearTokens(); setUser(null);
          }
        }
        // Network errors: keep tokens, user stays null but can retry on next load
      })
      .finally(() => setLoading(false));
  }, [refreshAccessToken]);

  // Auto-refresh before expiry (every 12 minutes)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      refreshAccessToken();
    }, 12 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshAccessToken]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch { /* silent */ }
  }, []);

  // login/refresh/seleção de conta devolvem `_user_response`, que não traz
  // `mfa_blocked` (só o /auth/me traz) ⇒ hidrata para o banner do 2FA não sumir.
  // Só publica se ainda houver sessão (logout no meio não é desfeito) e sempre
  // com a chave presente (resposta sem ela não reabre o efeito em loop).
  useEffect(() => {
    if (!user || "mfa_blocked" in user) return;
    api.get("/auth/me")
      .then((res) => setUser((prev) => (prev ? { ...res.data, mfa_blocked: !!res.data.mfa_blocked } : prev)))
      .catch(() => { /* silent */ });
  }, [user]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data.mfa_required || res.data.account_selection) {
      return res.data;
    }
    // Use Cognito token if available, otherwise legacy JWT
    const primaryToken = res.data.cognito_token || res.data.token;
    const refreshTk = res.data.refresh_token;
    saveTokens(primaryToken, refreshTk);
    if (res.data.cognito_token) {
      localStorage.setItem("auth_provider", "cognito");
      if (res.data.cognito_refresh_token) {
        localStorage.setItem("cognito_refresh_token", res.data.cognito_refresh_token);
      }
    } else {
      localStorage.removeItem("auth_provider");
      localStorage.removeItem("cognito_refresh_token");
    }
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await api.post("/auth/register", data);
    if (res.data.pending_approval) {
      return res.data;
    }
    saveTokens(res.data.token, res.data.refresh_token);
    setUser(res.data.user);
    return res.data;
  }, []);

  const loginMfa = useCallback(async (mfaToken, code, cognitoSession, email) => {
    const payload = cognitoSession
      ? { code, cognito_session: cognitoSession, email }
      : { mfa_token: mfaToken, code };
    const res = await api.post("/auth/login/mfa", payload);
    if (res.data.account_selection) return res.data;
    const primaryToken = res.data.cognito_token || res.data.token;
    const refreshTk = res.data.refresh_token;
    saveTokens(primaryToken, refreshTk);
    if (res.data.cognito_token) {
      localStorage.setItem("auth_provider", "cognito");
      if (res.data.cognito_refresh_token) {
        localStorage.setItem("cognito_refresh_token", res.data.cognito_refresh_token);
      }
    } else {
      localStorage.removeItem("auth_provider");
      localStorage.removeItem("cognito_refresh_token");
    }
    setUser(res.data.user);
    return res.data;
  }, []);

  const selectAccount = useCallback(async (userId, accountId, password, selectionToken) => {
    const res = await api.post("/auth/select-account", {
      user_id: userId, account_id: accountId,
      password: password || undefined,
      selection_token: selectionToken || undefined,
    });
    saveTokens(res.data.token, res.data.refresh_token);
    setUser(res.data.user);
    return res.data;
  }, []);

  const switchAccount = useCallback(async (accountId) => {
    const res = await api.post("/auth/switch-account", { account_id: accountId });
    saveTokens(res.data.token, res.data.refresh_token);
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginMfa, register, logout, refreshAccessToken, selectAccount, refreshUser, switchAccount }}>
      {children}
    </AuthContext.Provider>
  );
};
